"use client";

import Image from "next/image";
import React, { useState, useEffect } from "react";

const screenshots = [
  { src: "/1.png", alt: "WeeKrt App Screenshot 1" },
  { src: "/2.png", alt: "WeeKrt App Screenshot 2" },
  { src: "/3.png", alt: "WeeKrt App Screenshot 3" },
  { src: "/4.png", alt: "WeeKrt App Screenshot 4" },
  { src: "/5.png", alt: "WeeKrt App Screenshot 5" },
];

export default function ContentSection() {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Autoplay logic
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % screenshots.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isPaused]);

  // Pause on hover/touch
  const pause = () => setIsPaused(true);
  const resume = () => setIsPaused(false);

  // Navigation
  const prev = () =>
    setCurrent((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  const next = () => setCurrent((prev) => (prev + 1) % screenshots.length);

  return (
    <section className="py-32 md:py-56">
      <div className="mx-auto max-w-7xl space-y-24 px-6 md:space-y-32">
        <div>
          <h2 className="text-center text-5xl font-bold mb-24">
            See WeeKrt in Action
          </h2>
          <div
            className="relative flex items-center justify-center h-[440px] select-none"
            onMouseEnter={pause}
            onMouseLeave={resume}
            onTouchStart={pause}
            onTouchEnd={resume}
          >
            {/* Previous image (left) */}
            <button
              aria-label="Previous"
              onClick={prev}
              className="absolute left-0 z-10 h-full flex items-center px-2 md:px-4 focus:outline-none"
            >
              <span className="text-3xl md:text-5xl text-zinc-400 hover:text-zinc-600 dark:text-zinc-600 dark:hover:text-zinc-300 transition">
                &#8592;
              </span>
            </button>
            {/* Next image (right) */}
            <button
              aria-label="Next"
              onClick={next}
              className="absolute right-0 z-10 h-full flex items-center px-2 md:px-4 focus:outline-none"
            >
              <span className="text-3xl md:text-5xl text-zinc-400 hover:text-zinc-600 dark:text-zinc-600 dark:hover:text-zinc-300 transition">
                &#8594;
              </span>
            </button>
            <div className="relative w-full flex items-center justify-center h-[420px]">
              {screenshots.map((img, i) => {
                // Calculate position relative to current
                const offset = i - current;
                let style: React.CSSProperties = {
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  transition: "all 0.6s cubic-bezier(.4,1.7,.6,.97)",
                  zIndex: 1,
                  opacity: 0,
                  filter: "blur(0px)",
                  pointerEvents: "none",
                };
                if (offset === 0) {
                  // Center image
                  style = {
                    ...style,
                    zIndex: 3,
                    opacity: 1,
                    filter: "none",
                    transform: "translate(-50%, -50%) scale(1.4)",
                    pointerEvents: "auto",
                  };
                } else if (
                  offset === -1 ||
                  (offset === screenshots.length - 1 && current === 0)
                ) {
                  // Left image
                  style = {
                    ...style,
                    zIndex: 2,
                    opacity: 0.7,
                    filter: "blur(2px)",
                    transform: "translate(-90%, -50%) scale(0.92)",
                  };
                } else if (
                  offset === 1 ||
                  (offset === -(screenshots.length - 1) &&
                    current === screenshots.length - 1)
                ) {
                  // Right image
                  style = {
                    ...style,
                    zIndex: 2,
                    opacity: 0.7,
                    filter: "blur(2px)",
                    transform: "translate(-10%, -50%) scale(0.92)",
                  };
                }
                return (
                  <div
                    key={img.src}
                    style={style}
                    className="w-[380px] h-[400px] flex items-center justify-center overflow-hidden"
                  >
                    <Image
                      src={img.src}
                      alt={img.alt}
                      width={380}
                      height={400}
                      className="object-contain w-full h-full rounded-lg"
                      style={{ objectFit: "contain" }}
                      priority={i === 0}
                    />
                  </div>
                );
              })}
            </div>
            {/* Dots */}
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
              {screenshots.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    i === current
                      ? "bg-gradient-to-r from-green-400 to-blue-400 scale-125"
                      : "bg-zinc-300 dark:bg-zinc-700"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
